import { Injectable } from '@angular/core';
import {
  AbstractTuiValueTransformer,
  TuiDay,
  TuiDayRange,
} from '@taiga-ui/cdk';

class ExampleDateRangeTransformer extends AbstractTuiValueTransformer<
  TuiDayRange | null,
  [Date, Date] | null
> {
  constructor(
    private readonly dateTransformer: AbstractTuiValueTransformer<
      TuiDay | null,
      Date | null
    >
  ) {
    super();
  }

  fromControlValue(controlValue: [Date, Date] | null): TuiDayRange | null {
    // Si no se recibe ningún valor, retornar null
    if (!controlValue) {
      return null;
    }

    // Asegurarse de que ambos elementos sean objetos Date;
    // si no, intentar convertirlos.
    const transformedFrom =
      controlValue[0] instanceof Date
        ? controlValue[0]
        : new Date(controlValue[0]);
    const transformedTo =
      controlValue[1] instanceof Date
        ? controlValue[1]
        : new Date(controlValue[1]);

    // Validar que ambas fechas sean válidas
    if (isNaN(transformedFrom.getTime()) || isNaN(transformedTo.getTime())) {
      return null;
    }

    // Convertir las fechas a TuiDay usando el transformador inyectado.
    const from = this.dateTransformer.fromControlValue(transformedFrom);
    const to = this.dateTransformer.fromControlValue(transformedTo);

    // Retornar un nuevo TuiDayRange si ambas conversiones fueron exitosas.
    return from && to ? new TuiDayRange(from, to) : null;
  }

  toControlValue(componentValue: TuiDayRange | null): [Date, Date] | null {
    const from =
      componentValue &&
      this.dateTransformer.toControlValue(componentValue.from);
    const to =
      componentValue && this.dateTransformer.toControlValue(componentValue.to);

    return from && to ? [from, to] : null;
  }
}

@Injectable()
export class ExampleDateTransformer extends AbstractTuiValueTransformer<
  TuiDay | null,
  Date | null
> {
  fromControlValue(controlValue: Date | null): TuiDay | null {
    if (!controlValue) {
      return null;
    }
    // Si controlValue no es una instancia de Date, intenta convertirlo
    const dateObj =
      controlValue instanceof Date ? controlValue : new Date(controlValue);
    if (isNaN(dateObj.getTime())) {
      return null;
    }
    return TuiDay.fromLocalNativeDate(dateObj);
  }

  toControlValue(componentValue: TuiDay | null): Date | null {
    return componentValue?.toLocalNativeDate() || null;
  }
}

export function getExampleDateRangeTransformer(
  dateTransformer: ExampleDateTransformer | null
): AbstractTuiValueTransformer<TuiDayRange | null, [Date, Date] | null> | null {
  return dateTransformer
    ? new ExampleDateRangeTransformer(dateTransformer)
    : null;
}
